import React, { useEffect, useRef } from 'react';

const CHART_WIDTH = 680;
const CHART_HEIGHT = 400;

const SideBetsTotalsChart = ({
  d3,
  Plot,
  players = [],
  colorMap,
  fallbackColors,
  imageMap,
  loading,
  error,
  formatCurrency,
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (
      !d3 ||
      !Plot ||
      typeof Plot.plot !== 'function' ||
      typeof Plot.barY !== 'function' ||
      !containerRef.current ||
      !players.length
    ) {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      return undefined;
    }

    const container = containerRef.current;
    container.innerHTML = '';

    const fallbackIndexRef = { current: 0 };
    const getFillColor = (name) => {
      if (colorMap && colorMap[name]) return colorMap[name];
      if (!fallbackColors || !fallbackColors.length) return '#76ace0';
      const color = fallbackColors[fallbackIndexRef.current % fallbackColors.length];
      fallbackIndexRef.current += 1;
      return color;
    };

    const chart = Plot.plot({
      width: CHART_WIDTH,
      height: CHART_HEIGHT,
      style: { background: 'white' },
      marginBottom: 50,
      marginLeft: 80,
      x: {
        label: 'Player',
        domain: players.map((player) => player.name),
      },
      y: {
        label: 'Total',
        grid: true,
        labelOffset: 60,
        tickFormat: (value) => (formatCurrency ? formatCurrency(value) : value),
      },
      marks: [
        Plot.barY(players, {
          x: 'name',
          y: 'total',
          fill: (d) => getFillColor(d.name),
          title: (d) => `${d.name}: ${formatCurrency ? formatCurrency(d.total) : d.total}`,
        }),
      ],
    });

    container.appendChild(chart);
    const svgEl = container.querySelector('svg');
    if (svgEl) {
      const d3Svg = d3.select(svgEl);
      d3Svg.selectAll('text').style('font-size', '16px');
    }
    chart.style.width = '100%';
    chart.style.height = 'auto';

    const attachPlayerImagesToBars = () => {
      const svgEl = container.querySelector('svg');
      if (!svgEl) return;

      const d3Svg = d3.select(svgEl);
      d3Svg.select('g.player-images').remove();

      const rects = d3Svg.selectAll("g[aria-label='bar'] rect");
      if (rects.empty()) return;

      const imagesGroup = d3Svg.append('g').attr('class', 'player-images');

      rects.each(function (_, index) {
        const player = players[index];
        const href = imageMap ? imageMap[player?.name] : undefined;
        if (!player || !href) return;

        const bar = d3.select(this);
        const x = +bar.attr('x');
        const y = +bar.attr('y');
        const width = +bar.attr('width');
        const height = +bar.attr('height') || 0; // 0 for zero-value bars

        const imgW = 80;
        const imgH = 80;
        const centerX = x + width / 2;
        // For positive bars, y is top; for negative bars, y is baseline and height extends down.
        // Center is y + height/2 (and for 0-height, stays on baseline/axis).
        const centerY = y + (height > 0 ? height / 2 : 0);

        imagesGroup
          .append('image')
          .attr('href', href)
          .attr('x', centerX - imgW / 2)
          .attr('y', centerY - imgH / 2)
          .attr('width', imgW)
          .attr('height', imgH)
          .attr('preserveAspectRatio', 'xMidYMid meet')
          .style('pointer-events', 'none');
      });
    };

    attachPlayerImagesToBars();

    return () => {
      container.innerHTML = '';
    };
  }, [Plot, d3, players, colorMap, fallbackColors, imageMap, formatCurrency]);

  if (error) {
    return <p className="chart-error">Failed to load side bet totals. Please try again later.</p>;
  }

  if (loading) {
    return <p className="chart-loading">Loading side bet totals…</p>;
  }

  if (!players.length) {
    return <p className="chart-empty">No side bet totals are available yet.</p>;
  }

  return <div ref={containerRef} className="sidebets-chart" />;
};

export default SideBetsTotalsChart;
